import React, { useState, useCallback } from 'react';
import { Plus, Minus, X, Edit3, Check, Search, BadgeCheck, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const UNIT_OPTIONS = ['cups', 'oz', 'g', 'tbsp', 'tsp', 'ml', 'fl oz', 'slices', 'pieces', 'count', 'lb'];

export const EditableItemList = ({ items, onItemsChange, frequentItems = [] }) => {
  const [editingIdx, setEditingIdx] = useState(null);
  const [editName, setEditName] = useState('');
  const [showAddItem, setShowAddItem] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [lastRemoved, setLastRemoved] = useState(null);
  const [editingQtyIdx, setEditingQtyIdx] = useState(null);
  const [editQtyNum, setEditQtyNum] = useState('');
  const [editQtyUnit, setEditQtyUnit] = useState('');

  const recalcTotals = useCallback((updatedItems) => {
    onItemsChange(updatedItems);
  }, [onItemsChange]);

  const updateQuantityNum = (idx, delta) => {
    const updated = [...items];
    const item = { ...updated[idx] };
    const numMatch = item.quantity.match(/^([\d.]+)\s*(.*)/);
    if (numMatch) {
      const unit = numMatch[2] || '';
      // Use smart step: tbsp/tsp use 0.5, oz use 0.5, cups use 0.25, count use 1, g/ml use 10
      const step = ['tbsp','tsp','oz','fl oz'].includes(unit.toLowerCase()) ? 0.5
        : ['cups','cup'].includes(unit.toLowerCase()) ? 0.25
        : ['g','ml'].includes(unit.toLowerCase()) ? 10
        : 1;
      let num = parseFloat(numMatch[1]) + (delta * step);
      if (num < (step === 10 ? 10 : 0.25)) num = step === 10 ? 10 : 0.25;
      item.quantity = `${num} ${unit}`.trim();
      const ratio = num / parseFloat(numMatch[1]);
      item.calories = Math.round(item.calories * ratio);
      item.protein = Math.round(item.protein * ratio * 10) / 10;
      item.carbs = Math.round(item.carbs * ratio * 10) / 10;
      item.fat = Math.round(item.fat * ratio * 10) / 10;
    }
    updated[idx] = item;
    recalcTotals(updated);
  };

  const startEditQty = (idx) => {
    const numMatch = items[idx].quantity.match(/^([\d.]+)\s*(.*)/);
    setEditingQtyIdx(idx);
    setEditQtyNum(numMatch ? numMatch[1] : '1');
    setEditQtyUnit(numMatch ? numMatch[2].trim() : '');
  };

  const saveEditQty = () => {
    if (editingQtyIdx !== null && editQtyNum) {
      const updated = [...items];
      const item = { ...updated[editingQtyIdx] };
      const oldMatch = item.quantity.match(/^([\d.]+)/);
      const oldNum = oldMatch ? parseFloat(oldMatch[1]) : 1;
      const newNum = parseFloat(editQtyNum) || 1;
      const ratio = newNum / oldNum;
      item.quantity = `${newNum} ${editQtyUnit}`.trim();
      item.calories = Math.round(item.calories * ratio);
      item.protein = Math.round(item.protein * ratio * 10) / 10;
      item.carbs = Math.round(item.carbs * ratio * 10) / 10;
      item.fat = Math.round(item.fat * ratio * 10) / 10;
      updated[editingQtyIdx] = item;
      recalcTotals(updated);
    }
    setEditingQtyIdx(null);
  };

  const removeItem = (idx) => {
    const removed = items[idx];
    const updated = items.filter((_, i) => i !== idx);
    setLastRemoved({ item: removed, index: idx });
    recalcTotals(updated);
    toast('Item removed', {
      description: removed.name,
      action: {
        label: 'Undo',
        onClick: () => {
          const restored = [...updated];
          restored.splice(idx, 0, removed);
          recalcTotals(restored);
          setLastRemoved(null);
        },
      },
      duration: 5000,
    });
  };

  const startEditName = (idx) => {
    setEditingIdx(idx);
    setEditName(items[idx].name);
  };

  const saveEditName = () => {
    if (editingIdx !== null && editName.trim()) {
      const updated = [...items];
      updated[editingIdx] = { ...updated[editingIdx], name: editName.trim() };
      recalcTotals(updated);
    }
    setEditingIdx(null);
    setEditName('');
  };

  const addItem = (item) => {
    const updated = [...items, item];
    recalcTotals(updated);
    setShowAddItem(false);
    setSearchQuery('');
  };

  const addManualItem = () => {
    if (!searchQuery.trim()) return;
    addItem({
      name: searchQuery.trim(),
      quantity: '1 serving',
      calories: 100,
      protein: 5,
      carbs: 10,
      fat: 3,
    });
  };

  const filteredFrequent = frequentItems.filter(f =>
    f.name.toLowerCase().includes(searchQuery.toLowerCase())
  ).slice(0, 6);

  return (
    <div data-testid="editable-item-list">
      {/* Items */}
      <div className="space-y-2">
        {items.map((item, idx) => (
          <div
            key={idx}
            data-testid={`editable-item-${idx}`}
            className="bg-[#FAF8F5] dark:bg-[#141C22] rounded-xl p-4 group"
          >
            <div className="flex items-start gap-3">
              {/* Main content */}
              <div className="flex-1 min-w-0">
                {/* Food name - editable */}
                {editingIdx === idx ? (
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      data-testid={`edit-name-input-${idx}`}
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') saveEditName(); }}
                      className="flex-1 h-9 px-3 rounded-lg text-sm font-medium bg-white dark:bg-[#1E2A32] border border-[#1A3A4A]/20 dark:border-white/20 text-[#1A3A4A] dark:text-[#F0EDE8] focus:outline-none focus:ring-2 focus:ring-[#1A3A4A]/20"
                      autoFocus
                    />
                    <button onClick={saveEditName} className="p-1.5 rounded-lg bg-[#6B9E8A] text-white">
                      <Check className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 mb-1">
                    <button
                      data-testid={`item-name-${idx}`}
                      onClick={() => startEditName(idx)}
                      className="flex items-center gap-1.5 text-sm font-semibold text-[#1A3A4A] dark:text-[#F0EDE8] hover:text-[#6B9E8A] transition-colors"
                    >
                      {item.name}
                      <Edit3 className="w-3 h-3 opacity-0 group-hover:opacity-50" />
                    </button>
                    {item.verified && (
                      <span data-testid={`verified-badge-${idx}`} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-[#6B9E8A]/10 text-[#6B9E8A]">
                        <BadgeCheck className="w-3 h-3" /> Verified
                      </span>
                    )}
                  </div>
                )}
                {/* Brand + source label */}
                {item.brand && (
                  <p data-testid={`item-brand-${idx}`} className="text-[11px] text-[#9A8F87] mb-1 font-medium">
                    {item.brand}{item.verified_source ? ` · ${item.verified_source}` : ''}
                  </p>
                )}

                {/* Per-item macros */}
                <div className="flex items-center gap-3 text-xs text-[#7A7A72] dark:text-[#A1A1AA]">
                  <span className="font-medium text-[#1A3A4A] dark:text-[#F0EDE8]">{Math.round(item.calories)} cal</span>
                  <span>P:{Math.round(item.protein)}g</span>
                  <span>C:{Math.round(item.carbs)}g</span>
                  <span>F:{Math.round(item.fat)}g</span>
                </div>
                {item.unit_warning && (
                  <p data-testid={`unit-warning-${idx}`} className="text-[10px] text-[#C67A5C] mt-1 font-medium">
                    {item.unit_warning}
                  </p>
                )}
              </div>

              {/* Quantity stepper + unit picker */}
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  data-testid={`qty-minus-${idx}`}
                  onClick={() => updateQuantityNum(idx, -1)}
                  className="w-8 h-8 rounded-lg bg-[#1A3A4A]/5 dark:bg-white/5 flex items-center justify-center hover:bg-[#1A3A4A]/10 transition-colors"
                >
                  <Minus className="w-3.5 h-3.5 text-[#1A3A4A] dark:text-[#F0EDE8]" />
                </button>
                {editingQtyIdx === idx ? (
                  <div className="flex items-center gap-1">
                    <input
                      data-testid={`qty-edit-input-${idx}`}
                      type="number"
                      min="0.25"
                      step="0.25"
                      value={editQtyNum}
                      onChange={e => setEditQtyNum(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') saveEditQty(); }}
                      className="w-12 h-7 text-center text-xs font-medium rounded-md bg-white dark:bg-[#1E2A32] border border-[#1A3A4A]/20 text-[#1A3A4A] dark:text-[#F0EDE8] focus:outline-none focus:ring-1 focus:ring-[#6B9E8A]"
                      autoFocus
                    />
                    <select
                      data-testid={`qty-unit-select-${idx}`}
                      value={editQtyUnit}
                      onChange={e => setEditQtyUnit(e.target.value)}
                      className="h-7 text-[10px] rounded-md bg-white dark:bg-[#1E2A32] border border-[#1A3A4A]/20 text-[#1A3A4A] dark:text-[#F0EDE8] focus:outline-none cursor-pointer"
                    >
                      {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                      {editQtyUnit && !UNIT_OPTIONS.includes(editQtyUnit) && <option value={editQtyUnit}>{editQtyUnit}</option>}
                    </select>
                    <button onClick={saveEditQty} className="p-1 rounded bg-[#6B9E8A] text-white">
                      <Check className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <button
                    data-testid={`qty-display-${idx}`}
                    onClick={() => startEditQty(idx)}
                    className="w-20 text-center text-xs font-medium text-[#1A3A4A] dark:text-[#F0EDE8] truncate cursor-pointer hover:bg-[#1A3A4A]/5 rounded-lg py-1 px-1 transition-colors flex items-center justify-center gap-0.5"
                    title={`Click to edit: ${item.quantity}`}
                  >
                    <span className="truncate">{item.quantity}</span>
                    <ChevronDown className="w-2.5 h-2.5 opacity-40 flex-shrink-0" />
                  </button>
                )}
                <button
                  data-testid={`qty-plus-${idx}`}
                  onClick={() => updateQuantityNum(idx, 1)}
                  className="w-8 h-8 rounded-lg bg-[#1A3A4A]/5 dark:bg-white/5 flex items-center justify-center hover:bg-[#1A3A4A]/10 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5 text-[#1A3A4A] dark:text-[#F0EDE8]" />
                </button>
              </div>

              {/* Remove button */}
              <button
                data-testid={`remove-item-${idx}`}
                onClick={() => removeItem(idx)}
                className="p-1.5 rounded-lg text-[#7A7A72] hover:text-[#C45B5B] hover:bg-[#C45B5B]/10 transition-colors opacity-0 group-hover:opacity-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add Item */}
      {showAddItem ? (
        <div className="mt-3 bg-[#FAF8F5] dark:bg-[#141C22] rounded-xl p-4 animate-fade-in-up" data-testid="add-item-panel">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A7A72]" />
            <input
              data-testid="add-item-search"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addManualItem(); }}
              placeholder="Search or type food name..."
              className="w-full h-10 pl-10 pr-4 rounded-lg text-sm bg-white dark:bg-[#1E2A32] border border-[#1A3A4A]/10 dark:border-white/10 text-[#1A3A4A] dark:text-[#F0EDE8] focus:outline-none focus:ring-2 focus:ring-[#1A3A4A]/20"
              autoFocus
            />
          </div>

          {/* Frequent items chips */}
          {filteredFrequent.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {filteredFrequent.map((f, i) => (
                <button
                  key={i}
                  data-testid={`quick-add-${i}`}
                  onClick={() => addItem(f)}
                  className="px-3 py-1.5 rounded-full text-xs font-medium bg-[#1A3A4A]/5 dark:bg-white/5 text-[#1A3A4A] dark:text-[#F0EDE8] hover:bg-[#1A3A4A]/10 transition-colors"
                >
                  {f.name} · {Math.round(f.calories)}cal
                </button>
              ))}
            </div>
          )}

          {searchQuery.trim() && (
            <button
              data-testid="add-manual-item"
              onClick={addManualItem}
              className="w-full py-2.5 rounded-lg text-sm font-medium text-[#6B9E8A] hover:bg-[#6B9E8A]/5 transition-colors"
            >
              + Add "{searchQuery.trim()}" as custom item
            </button>
          )}

          <button
            onClick={() => { setShowAddItem(false); setSearchQuery(''); }}
            className="w-full text-center text-xs text-[#7A7A72] mt-2"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          data-testid="add-item-btn"
          onClick={() => setShowAddItem(true)}
          className="mt-3 w-full py-3 rounded-xl border-2 border-dashed border-[#1A3A4A]/10 dark:border-white/10 text-sm font-medium text-[#6B9E8A] hover:border-[#6B9E8A]/30 hover:bg-[#6B9E8A]/5 transition-all"
        >
          <Plus className="w-4 h-4 inline-block mr-1.5 -mt-0.5" /> Add item
        </button>
      )}
    </div>
  );
};
